import {
	OrderQuoteGuestAccessTokenStatus,
	useGetOrderQuoteGuestAccessTokensQuery,
	useRevokeOrderQuoteGuestAcceptanceLinkMutation,
} from '@codezo/graphql';
import { Check, Copy, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { GUEST_ACCESS_TOKEN_STATUS } from '../../../lib/status-configs';
import { formatDate } from '../../../lib/utils';
import { AnimatedHeight } from '../../ui/animated-height';
import { Button } from '../../ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';
import { StatusBadge } from '../../ui/status-badge';

/**
 * Every guest link on a quote, and the only way to revoke one.
 *
 * A link is a working credential to a client's quote — it logs its address in
 * without a password — so withdrawing one has to be reachable. The client card
 * keeps the design's single line, so this hangs off it rather than expanding it
 * into a panel.
 *
 * Links carry no expiry. Revoking is the only kill switch, which is why the
 * useful fact on a row is when it went out rather than when it lapses.
 */
export function ManageGuestLinksModal({
	open,
	onOpenChange,
	orderQuoteId,
	onChanged,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	orderQuoteId: string;
	/** Refetch the caller's own copy of the tokens — it renders the count. */
	onChanged: () => void;
}) {
	const [revokeId, setRevokeId] = useState<string | null>(null);
	const [copiedId, setCopiedId] = useState<string | null>(null);

	const { data, loading, refetch } = useGetOrderQuoteGuestAccessTokensQuery({
		variables: { orderQuoteId },
		fetchPolicy: 'no-cache',
		skip: !open,
	});
	const [revokeLink, { loading: revoking }] = useRevokeOrderQuoteGuestAcceptanceLinkMutation();

	const tokens = data?.getOrderQuoteGuestAccessTokens ?? [];
	// Only ACTIVE links still open anything; the rest are a paper trail.
	const active = tokens.filter((t) => t.status === OrderQuoteGuestAccessTokenStatus.Active);
	const past = tokens.filter((t) => t.status !== OrderQuoteGuestAccessTokenStatus.Active);

	const handleCopy = async (id: string, link: string) => {
		try {
			await navigator.clipboard.writeText(link);
			setCopiedId(id);
			setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
		} catch {
			toast.error('Could not copy the link');
		}
	};

	const handleRevoke = async (id: string) => {
		try {
			const { data } = await revokeLink({ variables: { id } });
			const res = data?.revokeOrderQuoteGuestAcceptanceLink;
			if (res && !res.success) throw new Error(res.message || 'Unknown error');
			toast.success('Link revoked');
			setRevokeId(null);
			await refetch();
			onChanged();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to revoke the link');
		}
	};

	const rows = [...active, ...past];

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Guest links</DialogTitle>
					<DialogDescription>
						Each link opens the whole quote for one email address, and stays valid until
						it is revoked.
					</DialogDescription>
				</DialogHeader>

				<DialogBody>
					<AnimatedHeight step={`${rows.length}:${revokeId ?? ''}:${loading}`}>
						{loading && rows.length === 0 ? (
							<div className="flex items-center gap-2 py-2 text-sm text-text-3">
								<Loader2 size={14} className="animate-spin" />
								Loading links…
							</div>
						) : rows.length === 0 ? (
							<div className="py-2 text-sm text-text-4">
								No links have been sent on this quote.
							</div>
						) : (
							<div className="flex flex-col gap-1.5">
								{rows.map((token) => {
									const config = GUEST_ACCESS_TOKEN_STATUS[token.status] ?? {
										label: token.status,
										tone: 'neutral' as const,
									};
									const isActive =
										token.status === OrderQuoteGuestAccessTokenStatus.Active;
									const confirming = revokeId === token.id;

									return (
										<div
											key={token.id}
											className="rounded-[var(--r-3)] border border-line-1 bg-surface-sunken px-3 py-2.5"
										>
											<div className="flex items-center gap-2.5">
												<span
													className="min-w-0 flex-1 truncate text-sm text-text-1"
													title={token.email}
												>
													{token.email}
												</span>
												<StatusBadge
													active
													activeLabel={config.label}
													activeTone={config.tone}
												/>
											</div>
											<div className="mt-0.5 text-xs text-text-4">
												{token.createdAt
													? `Sent ${formatDate(token.createdAt)}`
													: 'Sent'}
												{token.acceptedAt &&
													` · accepted ${formatDate(token.acceptedAt)}`}
												{token.revokedAt &&
													` · revoked ${formatDate(token.revokedAt)}`}
											</div>

											{isActive && !confirming && (
												<div className="mt-2 flex items-center gap-1">
													<Button
														variant="outline"
														size="sm"
														onClick={() =>
															handleCopy(token.id, token.link)
														}
													>
														{copiedId === token.id ? (
															<Check
																size={14}
																data-icon="inline-start"
															/>
														) : (
															<Copy
																size={14}
																data-icon="inline-start"
															/>
														)}
														{copiedId === token.id
															? 'Copied'
															: 'Copy link'}
													</Button>
													<Button
														variant="ghost"
														size="sm"
														onClick={() => setRevokeId(token.id)}
													>
														Revoke
													</Button>
												</div>
											)}

											{/* Confirm inline rather than in a second dialog —
											    this one is already a dialog, and the row being
											    acted on stays on screen. */}
											{isActive && confirming && (
												<div className="mt-2 flex flex-col gap-2 border-t border-line-1 pt-2">
													<div className="text-sm text-text-3">
														Revoke this link? {token.email} loses access
														to the quote immediately.
													</div>
													<div className="flex items-center gap-1">
														<Button
															variant="outline"
															size="sm"
															onClick={() => setRevokeId(null)}
															disabled={revoking}
														>
															Cancel
														</Button>
														<Button
															variant="destructive"
															size="sm"
															onClick={() => handleRevoke(token.id)}
															disabled={revoking}
														>
															{revoking ? 'Revoking…' : 'Revoke'}
														</Button>
													</div>
												</div>
											)}
										</div>
									);
								})}
							</div>
						)}
					</AnimatedHeight>
				</DialogBody>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Done
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
